import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

const MIN_QUERY_LENGTH = 3

// Remote JSON keys consumed by Tom Select itself; anything else is copied
// onto the selected <option>'s dataset (e.g. customer_name → data-customer-name)
// so other controllers can keep reading option data attributes.
const RESERVED_KEYS = ["value", "text", "search"]

export default class extends Controller {
  static values = {
    placeholder: String,
    noResultsText: String,
    searchPromptText: String,
    url: String
  }

  connect() {
    const config = {
      allowEmptyOption: true,
      create: false,
      maxOptions: null,
      plugins: ["dropdown_input"],
      placeholder: this.placeholderValue || this.element.dataset.placeholder || "",
      render: {
        no_results: () => `<div class="no-results">${this.noResultsTextValue}</div>`
      },
      onChange: (value) => {
        this.#syncSelectedOptionDataset(value)
        this.element.dispatchEvent(new Event("change", { bubbles: true }))
      }
    }

    if (this.hasUrlValue) Object.assign(config, this.#remoteConfig())

    this.tomSelect = new TomSelect(this.element, config)
  }

  disconnect() {
    this.tomSelect?.destroy()
  }

  #remoteConfig() {
    return {
      valueField: "value",
      labelField: "text",
      // "search" mirrors the server-side haystack so client-side filtering of
      // already-loaded options doesn't hide matches found by phone/legal id.
      searchField: ["text", "search"],
      shouldLoad: (query) => query.length >= MIN_QUERY_LENGTH,
      load: (query, callback) => this.#loadOptions(query, callback),
      render: {
        no_results: () => `<div class="no-results">${this.noResultsTextValue}</div>`,
        not_loading: () => `<div class="not-loading">${this.searchPromptTextValue}</div>`
      }
    }
  }

  #syncSelectedOptionDataset(value) {
    if (!this.hasUrlValue || !value) return

    const data = this.tomSelect?.options[value]
    const option = Array.from(this.element.options).find((o) => o.value === String(value))
    if (!data || !option) return

    Object.entries(data).forEach(([key, val]) => {
      if (key.startsWith("$") || RESERVED_KEYS.includes(key)) return
      option.dataset[this.#camelize(key)] = val ?? ""
    })
  }

  #camelize(key) {
    return key.replace(/_([a-z0-9])/g, (_, char) => char.toUpperCase())
  }

  async #loadOptions(query, callback) {
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url, { headers: { Accept: "application/json" } })
      callback(response.ok ? await response.json() : undefined)
    } catch {
      callback()
    }
  }
}
